import React from "react"
import { graphql } from "gatsby"

const DEFAULT_SITE_URL = "https://www.clevertrack.dk"

type SeoImage = {
  alt?: string | null
  url?: string | null
  fluid?: { src?: string | null } | null
}

type SeoProps = {
  title?: string | null
  schemaTitle?: string | null
  description?: string | null
  slug?: string | null
  lang?: string | null
  images?: Array<{ image?: SeoImage | null }> | SeoImage | null
  image?: SeoImage | null
  siteMetadata?: Record<string, any> | null
  siteSettings?: Record<string, any> | null
  pageType?: "WebPage" | "CollectionPage"
  ogType?: "website" | "article"
  hideFromGoogle?: boolean
  dates?: {
    published?: string | null
    modified?: string | null
  }
  article?: {
    title?: string | null
    description?: string | null
    published?: string | null
    modified?: string | null
    tags?: string[] | null
    author?: Record<string, any> | null
    image?: SeoImage | null
  } | null
}

const normalizePath = (value?: string | null) => {
  if (!value || value === "/") return "/"
  return `/${value}`.replace(/\/{2,}/g, "/").replace(/\/$/, "")
}

const absoluteUrl = (siteUrl: string, value?: string | null) => {
  if (!value) return undefined
  if (/^https?:\/\//i.test(value)) return value
  return `${siteUrl}${normalizePath(value)}`
}

const normalizeLanguage = (lang?: string | null) => {
  const [language = "da", territory = "DK"] = (lang || "da-DK").split("-")
  return `${language.toLowerCase()}-${territory.toUpperCase()}`
}

const openGraphLocale = (lang?: string | null) =>
  normalizeLanguage(lang).replace("-", "_")

const imageFromValue = (value?: SeoImage | null) => {
  if (!value) return undefined
  return value.url || value.fluid?.src || undefined
}

const resolveImages = (images: SeoProps["images"], image?: SeoImage | null) => {
  const values = Array.isArray(images)
    ? images.map((item) => item?.image).filter(Boolean)
    : images
    ? [images]
    : []

  if (image) values.unshift(image)

  return values
    .map((item) => ({ url: imageFromValue(item), alt: item?.alt || undefined }))
    .filter((item) => item.url)
}

const socialProfiles = (siteSettings?: Record<string, any> | null) =>
  (siteSettings?.data?.social_media_links || [])
    .map((item) => item?.social_media_profile_url?.url)
    .filter(Boolean)

const personSchema = (author?: Record<string, any> | null) => {
  const data = author?.document?.data || author?.data || author
  if (!data) return undefined

  const name = [data.first_name, data.last_name].filter(Boolean).join(" ")
  if (!name) return undefined

  const sameAs = (data.social_media_links || [])
    .map((item) => item?.social_media_profile_url?.url)
    .filter(Boolean)
  const image = imageFromValue(data.image)

  return {
    "@type": "Person",
    name,
    ...(data.role ? { jobTitle: data.role } : {}),
    ...(image ? { image } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  }
}

const serializeJsonLd = (value: Record<string, any>) =>
  JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029")

const createSchemaGraph = ({
  title,
  description,
  canonicalUrl,
  language,
  pageType,
  images,
  siteUrl,
  logo,
  profiles,
  dates,
  article,
}) => {
  const organizationId = `${siteUrl}/#organization`
  const websiteId = `${siteUrl}/#website`
  const webpageId = `${canonicalUrl}#webpage`
  const articleId = `${canonicalUrl}#article`
  const primaryImage = images[0]?.url
  const author = personSchema(article?.author)

  const organization = {
    "@type": "Organization",
    "@id": organizationId,
    name: "Clevertrack",
    url: `${siteUrl}/`,
    ...(logo
      ? {
          logo: {
            "@type": "ImageObject",
            url: absoluteUrl(siteUrl, logo),
          },
        }
      : {}),
    ...(profiles.length ? { sameAs: profiles } : {}),
  }

  const website = {
    "@type": "WebSite",
    "@id": websiteId,
    url: `${siteUrl}/`,
    name: "Clevertrack",
    inLanguage: language,
    publisher: { "@id": organizationId },
  }

  const webpage = {
    "@type": pageType,
    "@id": webpageId,
    url: canonicalUrl,
    name: title,
    description,
    inLanguage: language,
    isPartOf: { "@id": websiteId },
    about: { "@id": organizationId },
    ...(primaryImage
      ? { primaryImageOfPage: { "@type": "ImageObject", url: primaryImage } }
      : {}),
    ...(dates?.published ? { datePublished: dates.published } : {}),
    ...(dates?.modified ? { dateModified: dates.modified } : {}),
    ...(article ? { mainEntity: { "@id": articleId } } : {}),
  }

  const graph: Record<string, any>[] = [organization, website, webpage]

  if (article) {
    const articleImage = imageFromValue(article.image) || primaryImage
    graph.push({
      "@type": "BlogPosting",
      "@id": articleId,
      mainEntityOfPage: { "@id": webpageId },
      headline: article.title || title,
      description: article.description || description,
      inLanguage: language,
      ...(articleImage ? { image: [articleImage] } : {}),
      ...(article.published ? { datePublished: article.published } : {}),
      ...(article.modified ? { dateModified: article.modified } : {}),
      author: author || { "@id": organizationId },
      publisher: { "@id": organizationId },
      ...(article.tags?.length ? { keywords: article.tags } : {}),
    })
  }

  return { "@context": "https://schema.org", "@graph": graph }
}

const SeoHead = ({
  title,
  schemaTitle,
  description,
  slug,
  lang,
  images,
  image,
  siteMetadata,
  siteSettings,
  pageType = "WebPage",
  ogType = "website",
  hideFromGoogle = false,
  dates,
  article,
}: SeoProps) => {
  const metadata = siteMetadata || {}
  const siteUrl = (metadata.siteUrl || DEFAULT_SITE_URL).replace(/\/$/, "")
  const path = normalizePath(slug)
  const canonicalUrl = `${siteUrl}${path}`
  const language = normalizeLanguage(lang || metadata.lang)
  const resolvedTitle = title || metadata.title || "Clevertrack"
  const fullTitle = [
    metadata.metaTitlePrefix,
    resolvedTitle,
    metadata.metaTitleAppendix,
  ]
    .filter(Boolean)
    .join(" | ")
  const resolvedDescription = description || metadata.description || ""
  const resolvedImages = resolveImages(images, image)
  const primaryImage = resolvedImages[0]
  const profiles = socialProfiles(siteSettings)
  const noIndex =
    hideFromGoogle || process.env.GATSBY_ACTIVE_ENV !== "production"
  const graph = createSchemaGraph({
    title: schemaTitle || resolvedTitle,
    description: resolvedDescription,
    canonicalUrl,
    language,
    pageType,
    images: resolvedImages,
    siteUrl,
    logo: metadata.logo,
    profiles,
    dates,
    article,
  })

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {primaryImage && <meta name="image" content={primaryImage.url} />}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="describedby" href="/llms.txt" />
      <meta
        name="robots"
        content={noIndex ? "noindex,nofollow" : "index,follow"}
      />

      <meta property="og:locale" content={openGraphLocale(language)} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Clevertrack" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      {primaryImage && <meta property="og:image" content={primaryImage.url} />}
      {primaryImage?.alt && (
        <meta property="og:image:alt" content={primaryImage.alt} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {primaryImage && <meta name="twitter:image" content={primaryImage.url} />}
      {primaryImage?.alt && (
        <meta name="twitter:image:alt" content={primaryImage.alt} />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(graph) }}
      />
      <script
        id="Cookiebot"
        src="https://consent.cookiebot.com/uc.js"
        data-cbid="859afa55-df6a-4946-847e-ef081acc94e0"
        data-blockingmode="manual"
        type="text/javascript"
      />
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=UA-171398598-1"
      />
      <script
        defer
        data-domain="clevertrack.dk"
        src="https://plausible.io/js/script.js"
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'UA-171398598-1');`,
        }}
      />
      <script
        nonce="OeNrp1S3PckJ/Rx3"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
            n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-P397QJD');`,
        }}
      />
      <script
        data-cookieconsent="statistics"
        dangerouslySetInnerHTML={{
          __html: `(function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:2804378,hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
        }}
      />
      <script
        data-cookieconsent="marketing"
        type="text/javascript"
        id="hs-script-loader"
        async
        defer
        src="//js.hs-scripts.com/7430847.js"
      />
    </>
  )
}

export { SeoHead, normalizePath, serializeJsonLd }
export default SeoHead

export const query = graphql`
  fragment SiteSeoMetadata on Site {
    siteMetadata {
      title
      description
      author
      siteUrl
      lang
      logo
      facebook
      twitter
      metaTitlePrefix
      metaTitleAppendix
    }
  }
`
